import { useEffect, useMemo, useRef } from "react";

import { useAuthUserStream } from "~/auth/useAuth";
import { useDocumentBaseSpecStream, useDocumentBaseStream, useDocumentMetadata } from "~/state";
import { usePageStorage } from "~/state/machines/pageStorageMachine/index";
import { LOCAL_WORKSPACE_NAME } from "~/state/types";

import { useComputeCells } from "../hooks/useComputeCells";
import { type EaselContextType } from "./EaselContext";
import { useQueryBuilderState } from "./query-builder/useQueryBuilderState";
import { CanvasRuntimeManager } from "./runtime";

/**
 * useCreateEasel
 *
 * ⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⠿⠿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿
 * ⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⠀⠀⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿
 * ⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⡟⠛⠛⢻⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿
 * ⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⠛⠛⠛⠛⠓⠒⠒⠚⠛⠛⠛⠛⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿
 * ⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿
 * ⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿
 * ⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿
 * ⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿
 * ⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿
 * ⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣤⣤⣤⣤⣤⣤⣤⣤⣤⣤⣤⣤⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿
 * ⣿⣿⣿⣿⣿⣿⣿⣿⣿⣀⣀⣀⣀⣀⣀⣀⣀⣀⣀⣀⣀⣀⣀⣿⣿⣿⣿⣿⣿⣿⣿⣿
 * ⣿⣿⣿⣿⣿⣿⣿⣿⣿⡿⠀⣾⣿⣿⣿⡏⢹⣿⣿⣿⣷⠀⢿⣿⣿⣿⣿⣿⣿⣿⣿⣿
 * ⣿⣿⣿⣿⣿⣿⣿⣿⣿⠇⢀⣿⣿⣿⣿⡇⢸⣿⣿⣿⣿⡀⠸⣿⣿⣿⣿⣿⣿⣿⣿⣿
 * ⣿⣿⣿⣿⣿⣿⣿⣿⡿⠀⣸⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣇⠀⢿⣿⣿⣿⣿⣿⣿⣿⣿
 * ⣿⣿⣿⣿⣿⣿⣿⣿⣷⣶⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣶⣾⣿⣿⣿⣿⣿⣿⣿⣿
 *
 * @description
 * hook for creating easel state that is provided by <Easel/>
 *
 * @see_also
 * description in `./Easel.tsx`
 */

export type UseCreateEaselOptions = {
	workspaceName: string;
	documentId: string;
	pageId: string;
	documentInteractionMode: "static" | "interactive";
};

export type UseCreateEaselFn = (options: UseCreateEaselOptions) => EaselContextType;

const runtimes = new Map<string, CanvasRuntimeManager>();
const makeKey = (workspaceName: string, documentId: string, pageId: string) =>
	`${workspaceName}:${documentId}:${pageId}`;

export const useCreateEasel: UseCreateEaselFn = ({
	workspaceName,
	documentId,
	pageId,
	documentInteractionMode,
}) => {
	const user = useAuthUserStream();
	const [documentBaseSpecs, setDocumentBaseSpec] = useDocumentBaseSpecStream(documentId);
	const baseStream = useDocumentBaseStream(documentId);

	const { compiledCells: cells } = useComputeCells(documentId, pageId);

	const [queryBuilderState, queryBuilderStateManager] = useQueryBuilderState();

	const document = useDocumentMetadata(documentId);

	const canvasRef = useRef(document ?? null);
	canvasRef.current = document ?? null;

	const { pageStorageBridge } = usePageStorage({ workspaceName, documentId, pageId });

	let runtime = runtimes.get(makeKey(workspaceName, documentId, pageId));
	if (!runtime) {
		runtime = new CanvasRuntimeManager(
			process.env.APP_ENV as NonNullable<MomentAppEnv>,
			pageStorageBridge,
			documentInteractionMode,
			user.data,
			documentBaseSpecs.data,
			setDocumentBaseSpec,
			baseStream.data
		);
		runtimes.set(makeKey(workspaceName, documentId, pageId), runtime);
	}

	// Effects

	/**
	 * When cellsMap changes, run "ensure cells" on the runtime object
	 */
	useEffect(() => {
		runtime?.ensureCells(cells);
	}, [runtime, cells]);

	return useMemo(
		() => ({
			workspaceName,
			documentId,
			pageId,

			// :: Canvas ::
			canvasRef,

			// :: Runtime ::
			runtime,

			// :: Query Builder ::
			queryBuilderState,
			queryBuilderStateManager,

			canvasWritable: workspaceName === LOCAL_WORKSPACE_NAME,
			prosemirrorWritable: workspaceName === LOCAL_WORKSPACE_NAME,
		}),
		[workspaceName, documentId, pageId, runtime, queryBuilderState, queryBuilderStateManager]
	);
};
